import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';

export default function ServiceCatalog() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', service_type: 'one_time', delivery_module: 'none', category_id: '', sales_availability: 'available', status: 'draft', indicative_price: '', notes: '' });
  const [categories, setCategories] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchServices = () => {
    if (!companyId) return;
    setLoading(true);
    let url = `${API}/companies/${companyId}/services/catalog?page=${page}&per_page=25`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    if (statusFilter) url += `&status=${statusFilter}`;
    if (typeFilter) url += `&service_type=${typeFilter}`;

    fetch(url, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : { data: [] })
      .then(d => {
        setServices(Array.isArray(d.data) ? d.data : []);
        setTotalPages(d.last_page || 1);
      })
      .catch(() => setServices([]))
      .finally(() => setLoading(false));
  };

  const fetchCategories = () => {
    if (!companyId) return;
    fetch(`${API}/companies/${companyId}/services/categories`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(d => setCategories(Array.isArray(d) ? d : []))
      .catch(() => {});
  };

  useEffect(() => { fetchServices(); fetchCategories(); }, [companyId, page]);

  const handleSubmit = () => {
    const method = editing ? 'PUT' : 'POST';
    const url = editing
      ? `${API}/companies/${companyId}/services/catalog/${editing.id}`
      : `${API}/companies/${companyId}/services/catalog`;
    fetch(url, { method, headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(form) })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) { setShowForm(false); setEditing(null); resetForm(); fetchServices(); } })
      .catch(() => {});
  };

  const resetForm = () => setForm({ name: '', description: '', service_type: 'one_time', delivery_module: 'none', category_id: '', sales_availability: 'available', status: 'draft', indicative_price: '', notes: '' });

  const openEdit = (s) => {
    setForm({ name: s.name || '', description: s.description || '', service_type: s.service_type || 'one_time', delivery_module: s.delivery_module || 'none', category_id: s.category_id || '', sales_availability: s.sales_availability || 'available', status: s.status || 'draft', indicative_price: s.indicative_price || '', notes: s.notes || '' });
    setEditing(s);
    setShowForm(true);
  };

  const viewDetail = (id) => {
    fetch(`${API}/companies/${companyId}/services/catalog/${id}`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(d => setDetail(d))
      .catch(() => {});
  };

  const doAction = (id, action) => {
    fetch(`${API}/companies/${companyId}/services/catalog/${id}/${action}`, { method: 'POST', headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(() => { fetchServices(); if (detail && detail.id === id) viewDetail(id); })
      .catch(() => {});
  };

  const doDelete = (id) => {
    if (!window.confirm('Delete this draft service?')) return;
    fetch(`${API}/companies/${companyId}/services/catalog/${id}`, { method: 'DELETE', headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(() => fetchServices())
      .catch(() => {});
  };

  const fmt = (v) => '$' + Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2 });
  const cardStyle = { background: '#fff', borderRadius: 10, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: 20 };
  const thStyle = { textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #e2e8f0', fontSize: '0.8rem', color: '#64748b', fontWeight: 600 };
  const tdStyle = { padding: '10px 12px', borderBottom: '1px solid #f1f5f9', fontSize: '0.85rem' };
  const btnStyle = (bg) => ({ padding: '4px 12px', background: bg, color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: '0.78rem', marginRight: 4 });
  const inputStyle = { padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.85rem', width: '100%', boxSizing: 'border-box' };
  const badgeStyle = (color) => ({ display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: '0.75rem', fontWeight: 600, background: color + '20', color });
  const statusColor = { draft: '#94a3b8', active: '#22c55e', inactive: '#f59e0b', archived: '#6b7280' };

  if (detail) {
    return (
      <div style={{ padding: '24px 32px', maxWidth: 1200, margin: '0 auto' }}>
        <button onClick={() => setDetail(null)} style={{ ...btnStyle('#6b7280'), marginBottom: 16, padding: '6px 16px' }}>← Back</button>
        <div style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
            <div>
              <h2 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 700 }}>{detail.name}</h2>
              <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>{detail.service_code}</span>
              <span style={{ ...badgeStyle(statusColor[detail.status] || '#94a3b8'), marginLeft: 12 }}>{detail.status}</span>
            </div>
            <div>
              {detail.status === 'draft' && <button onClick={() => doAction(detail.id, 'activate')} style={btnStyle('#22c55e')}>Activate</button>}
              {detail.status === 'active' && <button onClick={() => doAction(detail.id, 'deactivate')} style={btnStyle('#f59e0b')}>Deactivate</button>}
              {(detail.status === 'inactive' || detail.status === 'active') && <button onClick={() => doAction(detail.id, 'archive')} style={btnStyle('#6b7280')}>Archive</button>}
              {detail.status === 'archived' && <button onClick={() => doAction(detail.id, 'restore')} style={btnStyle('#3b82f6')}>Restore</button>}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 20 }}>
            <div><span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Type</span><div style={{ fontWeight: 600 }}>{(detail.service_type || '').replace('_', ' ')}</div></div>
            <div><span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Delivery Module</span><div style={{ fontWeight: 600 }}>{(detail.delivery_module || '').replace('_', ' ')}</div></div>
            <div><span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Category</span><div style={{ fontWeight: 600 }}>{detail.category?.name || '—'}</div></div>
            <div><span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Owner</span><div style={{ fontWeight: 600 }}>{detail.service_owner?.name || '—'}</div></div>
            <div><span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Indicative Price</span><div style={{ fontWeight: 600 }}>{fmt(detail.indicative_price)}</div></div>
            <div><span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Sales</span><div style={{ fontWeight: 600 }}>{detail.sales_availability || '—'}</div></div>
          </div>
          {detail.description && <div style={{ marginBottom: 16 }}><span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Description</span><p style={{ margin: '4px 0', fontSize: '0.9rem' }}>{detail.description}</p></div>}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 20 }}>
          <div style={{ ...cardStyle, borderLeft: '4px solid #3b82f6' }}><div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Total Engagements</div><div style={{ fontSize: '1.3rem', fontWeight: 700 }}>{detail.total_engagements || 0}</div></div>
          <div style={{ ...cardStyle, borderLeft: '4px solid #22c55e' }}><div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Collected Revenue</div><div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#22c55e' }}>{fmt(detail.collected_revenue)}</div></div>
          <div style={{ ...cardStyle, borderLeft: '4px solid #8b5cf6' }}><div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Margin</div><div style={{ fontSize: '1.3rem', fontWeight: 700, color: detail.operational_margin >= 0 ? '#22c55e' : '#ef4444' }}>{fmt(detail.operational_margin)}</div></div>
        </div>

        <div style={cardStyle}>
          <h3 style={{ margin: '0 0 12px', fontSize: '1rem', fontWeight: 600 }}>Recent Engagements</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={thStyle}>Code</th><th style={thStyle}>Client</th><th style={thStyle}>Status</th><th style={thStyle}>Revenue</th></tr></thead>
            <tbody>
              {Array.isArray(detail.recent_engagements) && detail.recent_engagements.map(e => (
                <tr key={e.id}><td style={tdStyle}>{e.engagement_code}</td><td style={tdStyle}>{e.client_name}</td><td style={tdStyle}><span style={badgeStyle(statusColor[e.status] || '#94a3b8')}>{e.status}</span></td><td style={tdStyle}>{fmt(e.expected_revenue)}</td></tr>
              ))}
              {(!detail.recent_engagements || detail.recent_engagements.length === 0) && <tr><td colSpan={4} style={{ ...tdStyle, textAlign: 'center', color: '#94a3b8' }}>No engagements</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#1e293b' }}>📦 Service Catalog</h2>
        <button onClick={() => { resetForm(); setEditing(null); setShowForm(true); }} style={{ padding: '8px 20px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>+ New Service</button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <input placeholder="Search services..." value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && fetchServices()} style={{ ...inputStyle, maxWidth: 260 }} />
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} style={{ ...inputStyle, maxWidth: 160 }}>
          <option value="">All Statuses</option>
          <option value="draft">Draft</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="archived">Archived</option>
        </select>
        <select value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setPage(1); }} style={{ ...inputStyle, maxWidth: 160 }}>
          <option value="">All Types</option>
          <option value="one_time">One-Time</option>
          <option value="fixed_term">Fixed Term</option>
          <option value="recurring">Recurring</option>
          <option value="ongoing">Ongoing</option>
        </select>
        <button onClick={() => { setPage(1); fetchServices(); }} style={{ ...btnStyle('#3b82f6'), padding: '8px 16px' }}>Search</button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 32, width: 560, maxHeight: '80vh', overflowY: 'auto' }}>
            <h3 style={{ margin: '0 0 20px', fontSize: '1.2rem', fontWeight: 700 }}>{editing ? 'Edit Service' : 'New Service'}</h3>
            <div style={{ display: 'grid', gap: 14 }}>
              <div><label style={{ fontSize: '0.8rem', color: '#64748b', display: 'block', marginBottom: 4 }}>Name *</label><input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={inputStyle} /></div>
              <div><label style={{ fontSize: '0.8rem', color: '#64748b', display: 'block', marginBottom: 4 }}>Description</label><textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} style={{ ...inputStyle, minHeight: 60 }} /></div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div><label style={{ fontSize: '0.8rem', color: '#64748b', display: 'block', marginBottom: 4 }}>Service Type</label>
                  <select value={form.service_type} onChange={e => setForm({ ...form, service_type: e.target.value })} style={inputStyle}>
                    <option value="one_time">One-Time</option><option value="fixed_term">Fixed Term</option><option value="recurring">Recurring</option><option value="ongoing">Ongoing</option>
                  </select>
                </div>
                <div><label style={{ fontSize: '0.8rem', color: '#64748b', display: 'block', marginBottom: 4 }}>Delivery Module</label>
                  <select value={form.delivery_module} onChange={e => setForm({ ...form, delivery_module: e.target.value })} style={inputStyle}>
                    <option value="none">None</option><option value="project">Project</option><option value="outsourcing">Outsourcing</option><option value="academy">Academy</option><option value="services_only">Services Only</option><option value="other">Other</option>
                  </select>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div><label style={{ fontSize: '0.8rem', color: '#64748b', display: 'block', marginBottom: 4 }}>Category</label>
                  <select value={form.category_id} onChange={e => setForm({ ...form, category_id: e.target.value })} style={inputStyle}>
                    <option value="">— None —</option>
                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div><label style={{ fontSize: '0.8rem', color: '#64748b', display: 'block', marginBottom: 4 }}>Sales Availability</label>
                  <select value={form.sales_availability} onChange={e => setForm({ ...form, sales_availability: e.target.value })} style={inputStyle}>
                    <option value="available">Available</option><option value="limited">Limited</option><option value="unavailable">Unavailable</option><option value="internal_only">Internal Only</option>
                  </select>
                </div>
              </div>
              <div><label style={{ fontSize: '0.8rem', color: '#64748b', display: 'block', marginBottom: 4 }}>Indicative Price</label><input type="number" step="0.01" value={form.indicative_price} onChange={e => setForm({ ...form, indicative_price: e.target.value })} style={inputStyle} /></div>
              <div><label style={{ fontSize: '0.8rem', color: '#64748b', display: 'block', marginBottom: 4 }}>Notes</label><textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} style={{ ...inputStyle, minHeight: 50 }} /></div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
              <button onClick={() => { setShowForm(false); setEditing(null); }} style={{ ...btnStyle('#6b7280'), padding: '8px 20px' }}>Cancel</button>
              <button onClick={handleSubmit} style={{ ...btnStyle('#3b82f6'), padding: '8px 20px' }}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Services Table */}
      {initialLoading ? <div style={{ textAlign: 'center', padding: 40 }}>Loading...</div> : (
        <div style={cardStyle}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Service</th>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Category</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Engagements</th>
                <th style={thStyle}>Clients</th>
                <th style={thStyle}>Revenue</th>
                <th style={thStyle}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {services.map(s => (
                <tr key={s.id}>
                  <td style={tdStyle}><span style={{ fontWeight: 600, cursor: 'pointer', color: '#3b82f6' }} onClick={() => viewDetail(s.id)}>{s.name}</span><br /><span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{s.service_code}</span></td>
                  <td style={tdStyle}>{(s.service_type || '').replace('_', ' ')}</td>
                  <td style={tdStyle}>{s.category?.name || '—'}</td>
                  <td style={tdStyle}><span style={badgeStyle(statusColor[s.status] || '#94a3b8')}>{s.status}</span></td>
                  <td style={tdStyle}>{s.active_engagements || 0}</td>
                  <td style={tdStyle}>{s.clients || 0}</td>
                  <td style={tdStyle}>{fmt(s.collected_revenue)}</td>
                  <td style={tdStyle}>
                    <button onClick={() => openEdit(s)} style={btnStyle('#f59e0b')}>Edit</button>
                    {s.status === 'draft' && <button onClick={() => doDelete(s.id)} style={btnStyle('#ef4444')}>Delete</button>}
                  </td>
                </tr>
              ))}
              {services.length === 0 && <tr><td colSpan={8} style={{ ...tdStyle, textAlign: 'center', color: '#94a3b8' }}>No services found</td></tr>}
            </tbody>
          </table>
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
              <button disabled={page <= 1} onClick={() => setPage(page - 1)} style={{ ...btnStyle('#6b7280'), opacity: page <= 1 ? 0.5 : 1 }}>Prev</button>
              <span style={{ padding: '4px 12px', fontSize: '0.85rem' }}>Page {page} of {totalPages}</span>
              <button disabled={page >= totalPages} onClick={() => setPage(page + 1)} style={{ ...btnStyle('#6b7280'), opacity: page >= totalPages ? 0.5 : 1 }}>Next</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
